import {
  AssociationType,
  EventGroup,
  OpportunityOccurrencePersonGroup,
  OpportunityResponse,
  OpportunityResponseWithRecurrenceInfo,
} from 'Event/interfaces/interfaceCreateEditEvent'
import { getEventPublishDisplayStatus, getLegacyEventOverviewStatus } from 'Event/helpers/getEventPublishDisplayStatus'
import { EventDrilldownSectionHeader } from 'Event/components/EventDrilldownSectionHeader'
import { EventQuestionSet } from 'Event/hooks/useGetEventQuestionSets'
import useEventDrilldownStyles from 'Event/hooks/useEventDrilldownStyles'
import { useHistory } from 'react-router'
import { useDateStrings } from 'Event/scenes/events/hooks/useDateStrings'
import { EventExpansionPanelSummary } from 'Event/components/EventExpansionPanelSummary'
import { Edit as EditIcon } from '@material-ui/icons'
import { EventExpansionPanelDetails } from 'Event/components/EventExpansionPanelDetails'
import { getDisplayAddressFromEvent } from 'civic-champs-shared/core/location/utils'
import { LocationAddressType } from 'locations/types'
import { EventExpansionPanel } from 'Event/components/EventExpansionPanel'
import React, { MouseEvent, TouchEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useExpansionPanelSummaryStyle } from 'Event/hooks/useExpansionPanelSummaryStyle'
import { EventVisibility } from 'Event/components/EventVisibility'
import OutlinedButton from 'civic-champs-shared/core/OutlinedButton'
import TabletMacIcon from '@material-ui/icons/TabletMac'
import { useDispatch } from 'react-redux'
import { startKiosk } from 'kiosk/actions'
import { RECURRING_TYPE } from 'civic-champs-shared/core/utils/constants/RECURRING_TYPE_OPTIONS'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import useCancelEventPrompt from 'Event/modules/events/details/header/useCancelEventPrompt'
import useCancelOpportunities from 'Event/hooks/useCancelOpportunities'
import { CancelType } from 'Event/modules/events/details/header'
import { Summary, SummaryTable } from 'Event/components/SummaryTable'
import { useShowEventAddEditPrompt } from 'Event/hooks/useShowEventAddEditPrompt'
import { linkify } from 'civic-champs-shared/utils/linkify'
import { useGetEventGroups } from 'Event/hooks/useGetEventGroups'
import Loader from 'Event/components/loader'
import { useFeatureEnabled } from '../../core/feature/hooks'
import { encodeOccurrenceFromEvent } from '../helpers/encodeOccurrence'
import { useCurrentOrg } from '../../civic-champs-shared/auth/hooks'
import { createMarkup } from '../../civic-champs-shared/core/utils/helpers'
import { useShowAddEditOfferPrompt } from '../hooks/useShowAddEditOfferPrompt'
import useAddEditLocationPrompt from '../../locations/hooks/useAddEditLocationPrompt'
import { useFetchOpportunityLocation } from '../../locations/hooks/useFetchOpportunityLocation'
import AddIcon from '@material-ui/icons/Add'
import { useShowTemplateAddEditPrompt } from 'volunteering/opportunities/hooks/useShowTemplateAddEditPrompt'
import { mapEventToTemplate } from 'Event/helpers/map-event-to-template'
import useGetEventQuestionSets from 'Event/hooks/useGetEventQuestionSets'
import { OpportunityVisibility } from 'Event/interfaces'
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import { Box, IconButton, Popover } from '@material-ui/core'
import { TablePopoverButton } from 'civic-champs-shared'
import { useBreakPoint } from 'civic-champs-shared/helpers/useBreakpoint'
import {
  getEffectiveAllowMultipleRegistrations,
  getEventRegistrationsPolicyLabel,
  mapToEventGroup,
} from '../helpers/add-edit-helpers'
import { useShowPrompt } from '../../civic-champs-shared/core/modal/hooks'
import { ChangeSchedulingModal } from '../modules/events/create-edit/basic-info/ChangeSchedulingModal'
import { useIsSingleEvent } from 'Event/hooks/useIsSingleEvent'

const breakPoint = 768

export const EventsDrilldownInfo = ({
  event,
  encodedOccurrence,
  scrollTop,
  refresh,
  summary,
  type = 'Event',
  personGroups: personGroupsProp,
  eventGroups: eventGroupsProp,
  questionSets: questionSetsProp,
  onEditEventInformation,
  openLegacyEdit,
  showSectionEdit = true,
}: {
  event: OpportunityResponseWithRecurrenceInfo
  encodedOccurrence: string
  scrollTop: number
  refresh?: () => void
  summary?: Summary
  type?: string
  personGroups?: OpportunityOccurrencePersonGroup[]
  eventGroups?: EventGroup[]
  questionSets?: EventQuestionSet[]
  onEditEventInformation?: () => void
  openLegacyEdit?: () => void
  showSectionEdit?: boolean
}) => {
  const classes = useEventDrilldownStyles()
  const isAllowMultipleRegistrationsPerVolunteerEnabled = useFeatureEnabled('AllowMultipleRegistrationsPerVolunteer')
  const [getEventGroups, { loading: eventGroupsLoading, result: personGroupsFromApi }] = useGetEventGroups()
  const [getQuestionSets, { result: questionSetsFromApi, loading: questionSetsLoading }] = useGetEventQuestionSets()
  const usesExternalSupplementaryData = personGroupsProp != null && questionSetsProp != null
  useEffect(() => {
    if (usesExternalSupplementaryData) {
      return
    }
    if (event && event.schedulable) {
      getQuestionSets(encodeOccurrenceFromEvent(event))
    }
  }, [event, getQuestionSets, usesExternalSupplementaryData])
  const isSimplifyRecurringEventRulesEnabled = useFeatureEnabled('SimplifyRecurringEventRules')
  const isEditSequenceEnabled = useFeatureEnabled('EditEventSequenceInfo')
  const needsGroups = event.visibility === OpportunityVisibility.SELECT_GROUPS_ONLY
  useEffect(() => {
    if (usesExternalSupplementaryData) {
      return
    }
    getEventGroups(event.id)
  }, [event.id, getEventGroups, usesExternalSupplementaryData])

  const personGroups = usesExternalSupplementaryData ? personGroupsProp! : personGroupsFromApi
  const eventGroups = usesExternalSupplementaryData ? eventGroupsProp! : personGroupsFromApi.map(mapToEventGroup)
  const questionSets = usesExternalSupplementaryData ? questionSetsProp! : questionSetsFromApi
  const { schedulable } = event
  const offersEnabled = useFeatureEnabled('StandaloneOffers')
  const isNewEditEnabled = useFeatureEnabled('NewEditEventUICalendarEvent')
  const isEditMultishiftFallbackEnabledEnabled = useFeatureEnabled('NewAddEventUILegacyFallback')
  const isOpportunityTemplatesEnabled = useFeatureEnabled('OpportunityTemplates')
  const isQuestionnaireAsSurveyEnabled = useFeatureEnabled('QuestionnaireAsSurvey')
  const isEventDrilldownDetailsEnabled = useFeatureEnabled('EventDrilldownDetailsSection')
  const isEventAutomatedMessagesEnabled = useFeatureEnabled('EventAutomatedMessages')
  const history = useHistory()
  const currentOrg = useCurrentOrg()
  const showTemplateAddEditPrompt = useShowTemplateAddEditPrompt()
  const { instanceStartString, timeRange, recurrencePattern, recurrenceStart, recurrenceEnd } = useDateStrings({
    event,
  })
  const onSuccess = useCallback(
    (event: OpportunityResponse) => {
      const url = event.schedulable
        ? `/events/${encodeOccurrenceFromEvent(event)}/${currentOrg.id}`
        : `/opportunities/${encodeOccurrenceFromEvent(event)}`
      if (history.location.pathname === url) {
        refresh?.()
      } else {
        history.push(url)
      }
    },
    [currentOrg.id, history, refresh],
  )

  const showChangeSchedulingModal = useShowPrompt(ChangeSchedulingModal)

  const createTemplate = useCallback(() => {
    const additionalData = {
      groups: eventGroups,
      waivers: (event.requiredCredentials || []).map(({ id, title }) => ({
        id,
        name: title,
      })),
      questionnaires: questionSets.map((eventQuestionSet: any) => ({
        id: isQuestionnaireAsSurveyEnabled ? eventQuestionSet.survey?.id : eventQuestionSet.questionSet.id,
        name: eventQuestionSet.questionSet.name,
      })),
    }
    return showTemplateAddEditPrompt(mapEventToTemplate(event), additionalData)
  }, [event, eventGroups, isQuestionnaireAsSurveyEnabled, questionSets, showTemplateAddEditPrompt])

  const showOpportunityPrompt = useShowEventAddEditPrompt(onSuccess)
  const showOfferPrompt = useShowAddEditOfferPrompt(onSuccess)
  const showLocationEditPrompt = useAddEditLocationPrompt()
  const { fetchLocation } = useFetchOpportunityLocation()
  const [expanded, setExpanded] = useState(true)
  const summaryRef = useRef<HTMLDivElement>(null)
  const detailsRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()
  const handleStartKiosk = useCallback(() => {
    if (event.isOffer) return
    dispatch(startKiosk(event))
  }, [event, dispatch])
  const cancelOpportunities = useCancelOpportunities()
  const handleCancelEvent = useCallback(
    async ({ cancelType }: { cancelType: CancelType }) => {
      await cancelOpportunities([encodedOccurrence], cancelType)
      history.push('/list-of-events')
    },
    [cancelOpportunities, encodedOccurrence, history],
  )

  const onEdit = useCallback(async () => {
    if (event.isOffer) {
      showOfferPrompt({ offer: event, visibilityGroups: personGroups, encodedOccurrence })
    } else if (
      event.schedulable &&
      (!isNewEditEnabled ||
        (isEditMultishiftFallbackEnabledEnabled && event.timeshifts?.length && event.timeshifts?.length > 1))
    ) {
      history.push(`/events/${encodedOccurrence}/edit-event/basic`)
    } else if (!event.schedulable && offersEnabled) {
      onSuccess((await showLocationEditPrompt(await fetchLocation(event.locationId), true)) as OpportunityResponse)
    } else {
      showOpportunityPrompt(event)
    }
  }, [
    encodedOccurrence,
    event,
    fetchLocation,
    personGroups,
    history,
    isEditMultishiftFallbackEnabledEnabled,
    isNewEditEnabled,
    offersEnabled,
    onSuccess,
    showLocationEditPrompt,
    showOfferPrompt,
    showOpportunityPrompt,
  ])

  const showCancelEventPrompt = useCancelEventPrompt(handleCancelEvent)
  const onCancel = useCallback(() => showCancelEventPrompt(event), [event, showCancelEventPrompt])

  const style = useExpansionPanelSummaryStyle({
    expanded,
    scrollTop,
    summaryRef,
    detailsRef,
  })

  const status = useMemo(
    () =>
      isEventDrilldownDetailsEnabled
        ? getEventPublishDisplayStatus(event, currentOrg)
        : getLegacyEventOverviewStatus(event, currentOrg),
    [currentOrg, event, isEventDrilldownDetailsEnabled],
  )

  const isSmall = useBreakPoint(breakPoint)

  const singleEvent = useIsSingleEvent(event)

  const onEditEventInfo = onEditEventInformation ?? openLegacyEdit ?? onEdit

  if (!usesExternalSupplementaryData && (eventGroupsLoading || questionSetsLoading)) {
    return <Loader />
  }

  return (
    <EventExpansionPanel onChange={(_, expanded) => setExpanded(expanded)}>
      <EventExpansionPanelSummary style={style} ref={summaryRef} name="event-info">
        <h1>Overview</h1>
        {isSmall && (
          <PopupState variant="popover" popupId="menu-buttons-popover">
            {popupState => {
              const { onClick, onTouchStart } = bindTrigger(popupState)
              return (
                <>
                  <IconButton
                    className={classes.popoverButton}
                    aria-label="show more"
                    component="span"
                    onClick={(e: MouseEvent<any>) => {
                      e.stopPropagation()
                      onClick(e)
                    }}
                    onTouchStart={(e: TouchEvent<any>) => {
                      e.stopPropagation()
                      onTouchStart(e)
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Popover
                    {...bindPopover(popupState)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    onClick={(e: MouseEvent<any>) => {
                      e.stopPropagation()
                    }}
                    onTouchStart={(e: TouchEvent<any>) => {
                      e.stopPropagation()
                    }}
                  >
                    <Box className={classes.popover} p={2}>
                      <TablePopoverButton startIcon={<TabletMacIcon />} onClick={handleStartKiosk} rounded>
                        Open Kiosk
                      </TablePopoverButton>
                      <TablePopoverButton startIcon={<EditIcon />} onClick={onEdit} rounded>
                        Edit Event
                      </TablePopoverButton>
                      <TablePopoverButton startIcon={<HighlightOffIcon />} onClick={onCancel} rounded>
                        Cancel {type}
                      </TablePopoverButton>
                      {schedulable && isOpportunityTemplatesEnabled && event.timeshifts?.length === 1 && (
                        <TablePopoverButton startIcon={<AddIcon />} onClick={createTemplate} rounded>
                          Save as Template
                        </TablePopoverButton>
                      )}
                    </Box>
                  </Popover>
                </>
              )
            }}
          </PopupState>
        )}
      </EventExpansionPanelSummary>
      <EventExpansionPanelDetails ref={detailsRef}>
        {!isSmall && (
          <div className={classes.buttons}>
            <OutlinedButton startIcon={<TabletMacIcon />} onClick={handleStartKiosk}>
              Open Kiosk
            </OutlinedButton>
            <div className={classes.rightButtons}>
              <OutlinedButton startIcon={<HighlightOffIcon />} onClick={onCancel}>
                Cancel {type}
              </OutlinedButton>
              {schedulable && isOpportunityTemplatesEnabled && event.timeshifts?.length === 1 && (
                <OutlinedButton startIcon={<AddIcon />} onClick={createTemplate}>
                  Save as Template
                </OutlinedButton>
              )}
            </div>
          </div>
        )}
        <div className={classes.details}>
          <div className={classes.eventInfo}>
            <EventDrilldownSectionHeader
              title={`${type} Information`}
              onEdit={
                showSectionEdit && !isSmall ? (isEventDrilldownDetailsEnabled ? onEditEventInfo : onEdit) : undefined
              }
            />
            {event.isOffer || (offersEnabled && !schedulable) ? (
              <>
                <div className={classes.sectionRow}>
                  <div>{type} Start:</div>
                  <div>{recurrenceStart}</div>
                </div>
                <div className={classes.sectionRow}>
                  <div>{type} End:</div>
                  <div>{recurrenceEnd}</div>
                </div>
              </>
            ) : (
              <>
                <div className={classes.sectionRow}>
                  <div>{type} Date:</div>
                  <div>{instanceStartString}</div>
                </div>
                <div className={classes.sectionRow}>
                  <div>{type} Time:</div>
                  <div>{timeRange}</div>
                </div>
              </>
            )}
            <div className={classes.sectionRow}>
              <div>Location:</div>
              <div>
                {event.locationAddressType === LocationAddressType.PlusCode
                  ? event.locationDetails
                  : event.locationIsAddress
                  ? getDisplayAddressFromEvent(event)
                  : event.locationDetails
                  ? linkify(event.locationDetails, 50)
                  : null}
              </div>
            </div>
            <div className={classes.sectionRow}>
              <div>Visibility:</div>
              <div>
                <EventVisibility
                  event={event}
                  visibilityGroups={
                    needsGroups
                      ? personGroups.filter(
                          ({ associationType }) => associationType === AssociationType.EVENT_PRIVATE_TO_MEMBERS,
                        )
                      : undefined
                  }
                />
              </div>
            </div>
            <div className={classes.sectionRow}>
              <div>Status:</div>
              <div>{status}</div>
            </div>
            {schedulable && isAllowMultipleRegistrationsPerVolunteerEnabled && (
              <div className={classes.sectionRow}>
                <div>Registrations:</div>
                <div>
                  {getEventRegistrationsPolicyLabel(
                    getEffectiveAllowMultipleRegistrations(
                      isAllowMultipleRegistrationsPerVolunteerEnabled,
                      event.allowMultipleRegistrations,
                    ),
                  )}
                </div>
              </div>
            )}
            {schedulable && !isEventDrilldownDetailsEnabled && (
              <>
                <EventDrilldownSectionHeader
                  title="Primary Point of Contact"
                  onEdit={showSectionEdit && !isSmall ? onEdit : undefined}
                />
                <div className={classes.sectionRow}>
                  <div>Name:</div>
                  <div>{event.contact_name}</div>
                </div>
                <div className={classes.sectionRow}>
                  <div>Phone:</div>
                  <div>{event.contact_phone_number}</div>
                </div>
                <div className={classes.sectionRow}>
                  <div>Email:</div>
                  <div>{event.contact_email}</div>
                </div>
              </>
            )}
            {schedulable && (event.recurrenceInfo.type !== RECURRING_TYPE.NEVER || !singleEvent) && (
              <>
                <EventDrilldownSectionHeader
                  title="Sequence Information"
                  onEdit={
                    showSectionEdit && !isSmall && isEditSequenceEnabled
                      ? isSimplifyRecurringEventRulesEnabled
                        ? () =>
                            showChangeSchedulingModal({
                              encodedOccurrence,
                              recurrenceInfo: event.recurrenceInfo,
                              sequenceInfo: event.sequenceInfo,
                            })
                        : isEventDrilldownDetailsEnabled
                        ? onEditEventInfo
                        : onEdit
                      : undefined
                  }
                />
                <div className={classes.sectionRow}>
                  <div>Start Date:</div>
                  <div>{recurrenceStart}</div>
                </div>
                <div className={classes.sectionRow}>
                  <div>End Date:</div>
                  <div>{recurrenceEnd}</div>
                </div>
                <div className={classes.sectionRow}>
                  <div>Frequency:</div>
                  <div>{recurrencePattern}</div>
                </div>
              </>
            )}
          </div>
          <div className={classes.description}>
            <h3>Description</h3>
            <div dangerouslySetInnerHTML={createMarkup(event.description)} />
            {summary && (
              <>
                <h3>{isEventAutomatedMessagesEnabled ? 'Day-Before Reminder' : 'Day-of Event Instructions'}</h3>
                <div dangerouslySetInnerHTML={createMarkup(event.instructions)} />
                <h3>Signups Summary</h3>
                <div className={classes.signupSummary}>
                  <SummaryTable summary={summary} />
                </div>
              </>
            )}
            {event.isOffer && event.trackSignups && (
              <>
                <h3>Post Signup Instructions</h3>
                <div dangerouslySetInnerHTML={createMarkup(event.instructions)} />
              </>
            )}
          </div>
          <div className={classes.support} />
        </div>
      </EventExpansionPanelDetails>
    </EventExpansionPanel>
  )
}
